import {FlatListDto, FlatListOrder, GraphTimeSpan, LogOverviewDto, Severity} from '../../swagger/models';
import {action, makeObservable, observable} from 'mobx';
import {ApiDashboardLogOverviewGetRequest} from '../../swagger/apis';
import {
    DefaultGraphTimeSpan,
    DefaultOrderBy,
    GraphTimeSpanField,
    OrderByField,
    PageField
} from './FlatListForLogGenericState';
import Cookies from 'js-cookie';
import {toggleInArrayImmutable} from '../../helpers/helpers';

export interface SearchOptions {
    value: string;
    firstSeenFrom?: Date;
    firstSeenTo?: Date;
    lastSeenFrom?: Date;
    lastSeenTo?: Date;
    severities?: Severity[];
}

export abstract class FlatListState {
    showSearch: boolean = true;
    showPagination: boolean = true;

    @observable
    pauseListRefresh: boolean = false;
    @observable
    currentFlatId?: string | null;
    @observable
    log?: LogOverviewDto;
    @observable
    showFullListButton: boolean;
    @observable
    advancedSearchExpanded: boolean = false;
    @observable
    currentOrder: FlatListOrder = FlatListOrder.LastSeen;
    @observable
    orderBy: FlatListOrder = DefaultOrderBy;
    @observable
    showUptimeEventsList = false;

    @observable
    listParams: ApiDashboardLogOverviewGetRequest = {
        logId: '',
        page: 1,
        limit: this.getMaxResults(),
        orderBy: DefaultOrderBy,
        graphTimeSpan: DefaultGraphTimeSpan,
    };

    abstract flatClickHandler(v: FlatListDto): void;

    abstract search(): void;

    abstract delete(): void;

    abstract loadList(loadLast?: boolean): Promise<any>;


    protected constructor(showFullListButton: boolean, showPagination: boolean) {
        makeObservable(this);
        this.showFullListButton = showFullListButton;
        this.showPagination = showPagination;
    }

    @action
    setCurrentLog(logId: string, flatId?: string) {
        this.listParams.logId = logId;
        this.currentFlatId = flatId;
        this.loadList();
    }

    protected getMaxResults() {
        const bottomBarVisible = !!(document.getElementById('bottombar')?.offsetParent);
        return Math.round((window.innerHeight - (bottomBarVisible ? 350 : 280)) / 84);
    }

    @action
    setSearchValue(value: string) {
        this.listParams.searchSearchValue = value;
    }

    @action
    toggleAdvancedSearchHandler() {
        this.advancedSearchExpanded = !this.advancedSearchExpanded;
    }

    protected saveQueryParams() {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set(PageField, this.listParams.page + '');
        searchParams.set(OrderByField, this.listParams.orderBy + '');
        searchParams.set(GraphTimeSpanField, this.listParams.graphTimeSpan + '');

        Cookies.set(OrderByField, this.listParams.orderBy + '');
        Cookies.set(GraphTimeSpanField, this.listParams.graphTimeSpan + '');

        const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString() + window.location.hash;
        // eslint-disable-next-line no-restricted-globals
        history.pushState(null, '', newRelativePathQuery);
    }

    protected loadQueryParams() {
        const searchParams = new URLSearchParams(window.location.search);
        const page = parseInt(searchParams.get(PageField) + '');

        this.listParams.orderBy = searchParams.get(OrderByField) as FlatListOrder ?? Cookies.get(OrderByField) ?? DefaultOrderBy;
        this.listParams.graphTimeSpan = searchParams.get(GraphTimeSpanField) as GraphTimeSpan ?? Cookies.get(GraphTimeSpanField) ?? DefaultGraphTimeSpan;
        this.listParams.page = page ? page : 1;
    }

    @action
    clearFilters() {
        this.listParams.searchSearchValue = undefined;
        this.listParams.searchFirstSeenFrom = undefined;
        this.listParams.searchFirstSeenTo = undefined;
        this.listParams.searchLastSeenFrom = undefined;
        this.listParams.searchLastSeenTo = undefined;
        this.listParams.searchSeverities = undefined;
    }

    @action
    setGraphTimeSpan(v: GraphTimeSpan) {
        this.listParams.graphTimeSpan = v;
        this.log!.graph = [];
        this.saveQueryParams();
        this.loadList();
    }

    @action
    setOrderBy(v: FlatListOrder) {
        this.orderBy = v;
        this.saveQueryParams();
        this.loadList();
    }

    @action
    changePage(v: number, loadLast?: boolean) {
        this.listParams.page = v;
        this.currentFlatId = undefined;
        this.saveQueryParams();
        this.loadList(loadLast);
    }

    @action
    setSearchFirstSeenFrom(v: Date | undefined) {
        this.listParams.searchFirstSeenFrom = v;
    }

    @action
    setSearchFirstSeenTo(v: Date | undefined) {
        this.listParams.searchFirstSeenTo = v;
    }

    @action
    setSearchLastSeenFrom(v: Date | undefined) {
        this.listParams.searchLastSeenFrom = v;
    }

    @action
    setSearchLastSeenTo(v: Date | undefined) {
        this.listParams.searchLastSeenTo = v;
    }

    @action
    toggleSearchSeveritiy(s: any) {
        if (!this.listParams.searchSeverities) {
            this.listParams.searchSeverities = [];
        }
        this.listParams.searchSeverities = toggleInArrayImmutable(this.listParams.searchSeverities, s);

        if (this.listParams.searchSeverities.length === 0) {
            this.listParams.searchSeverities = undefined;
        }
    }

    @action
    togglePauseListRefresh() {
        this.pauseListRefresh = !this.pauseListRefresh;
    }

    @action
    setShowUptimeEventsList(v: boolean) {
        this.showUptimeEventsList = v;
    }

    @action
    removeStructuralDataFilters() {
        this.listParams.searchStructuralDataFilters = undefined;
        this.loadList();
    }
}
